import React, { useEffect, useState } from "react";
import { GridToolbar } from "@material-ui/data-grid";
import { makeGETRequestOnUserService, makePUTRequestOnUserService } from "../../Api/api";
import { RouteComponentProps } from "react-router-dom";
import toastr from "toastr";
import { Box } from "@mui/material";
import { Liquidation } from "../RFQOrders/interfaces";
import moment from "moment";
import {
    DataGrid
} from "@material-ui/data-grid";
import { Grid } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import { Select, MenuItem } from "@mui/material";
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import GenericDialog from "../Dialog/GenericDialog";
import Skeleton from "@mui/material/Skeleton";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableContainer,
    IconButton,
    Button,
} from "@mui/material";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import CloseIcon from "@mui/icons-material/Close";

const LiquidationList: React.FC<RouteComponentProps> = () => {

    const [liquidation, setLiquidation] = useState<Liquidation[]>([]);
    const [totalLiquidationRequests, setTotalLiquidationRequests] = useState(0);
    const [totalRequests, setTotalRequests] = useState(0);
    const [executedRequests, setExecutedRequests] = useState(0);
    const [totalClients, setTotalClients] = useState(0);
    const [loading, setLoading] = React.useState(false);
    const [updatingRow, setUpdatingRow] = useState<string | null>(null);
    const [confirmStatusModal, setConfirmStatusModal] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState<string>("");
    const [selectedUUID, setSelectedUUID] = useState<string>("");
    const [breakdownDialogOpen, setBreakdownDialogOpen] = useState(false);

    const calculateTotals = (data: Liquidation[]) => {
        const faceValueTotal = data.reduce((sum, item) => {
            const value = parseFloat(item.proposed_face_value || "0");
            return sum + value;
        }, 0);

        const requestCount = data.length;

        const executedCount = data.filter(
            (item) => item.liquidation_status === "Executed"
        ).length;

        const uniqueClients = new Set(
            data.map((item) => item.smest_order?.user)
        ).size;

        setTotalLiquidationRequests(faceValueTotal);
        setTotalRequests(requestCount);
        setExecutedRequests(executedCount);
        setTotalClients(uniqueClients);
    };

    const getBreakdownByUserType = () => {
        const userTypes = Array.from(new Set(liquidation.map((item: any) => item.user_type || "unknown")));

        return userTypes.map((type) => {
            const filtered = liquidation.filter((item: any) => (item.user_type || "unknown") === type);

            const faceValueTotal = filtered.reduce((sum, item) => {
                return sum + parseFloat(item.proposed_face_value || "0");
            }, 0);

            const executedCount = filtered.filter(
                (item) => item.liquidation_status === "Executed"
            ).length;

            const uniqueClients = new Set(
                filtered.map((item) => item.smest_order?.user)
            ).size;

            return {
                user_type: type,
                totalLiquidationRequests: faceValueTotal,
                totalRequests: filtered.length,
                executedRequests: executedCount,
                totalClients: uniqueClients,
            };
        });
    };


    useEffect(() => {
        setLoading(true);
        makeGETRequestOnUserService("order_management_v2/liquidation_request_list/?user_type=all")
            .then((resp) => {
                if (resp.status === 200) {
                    const data: Liquidation[] = resp.data;

                    setLiquidation(data);
                    calculateTotals(data);
                } else {
                    toastr.error("Something went wrong");
                }
            })
            .catch((error) => toastr.error(error))
            .finally(() => setLoading(false));
    }, []);


    const openConfirmDialog = (uuid: string, newStatus: string) => {
        setSelectedUUID(uuid);
        setSelectedStatus(newStatus);
        setConfirmStatusModal(true);
    };

    const confirmStatusUpdate = async () => {
        if (!selectedUUID) return;
        setConfirmStatusModal(false);
        setUpdatingRow(selectedUUID);

        try {
            const response = await makePUTRequestOnUserService(
                `order_management_v2/liquidation_request_update/${selectedUUID}/`,
                { liquidation_status: selectedStatus }
            );

            if (response.status === 200) {
                toastr.success("Status updated successfully");

                setLiquidation(prev => {
                    const updated = prev.map(item =>
                        item.uuid === selectedUUID
                            ? { ...item, liquidation_status: selectedStatus }
                            : item
                    );
                    calculateTotals(updated);
                    return updated;
                });

            } else {
                toastr.error("Failed to update status");
            }

        } catch (error) {
            toastr.error("Error updating status");

        } finally {
            setUpdatingRow(null);
        }
    };


    const dataGridCells = [
        {
            field: "source",
            headerName: "Source",
            width: 180,
            editable: false,
            valueGetter: (params: any) => {
                const { user_type, source } = params.row;
                if (user_type === "partner") return `Self (${source?.company_name || source?.name || ""})`;
                if (user_type === "client") return `Client (${source?.company_name || source?.name || ""})`;
                if (user_type === "smest") return source?.name || "SMEST";
            },
        },
        {
            field: "insertedAt",
            headerName: "Date",
            width: 140,
            editable: false,
            renderCell: (params: any) => {
                const rowData = params.row;
                return moment(rowData.insertedAt).format("DD MMM, YYYY HH:mm");
            },
        },
        {
            field: "client_name",
            headerName: "Client Name",
            width: 160,
            editable: false,
        },
        {
            field: "proposed_settlement_date",
            headerName: "Proposed Settlement Date",
            width: 255,
            editable: false,
        },
        { field: "isin", headerName: "ISIN", width: 120, editable: false },
        {
            field: "security_name",
            headerName: "Security Name",
            width: 200,
            editable: false,
        },
        {
            field: "buy_price",
            headerName: "Buy Price",
            width: 145,
            editable: false,
        },
        {
            field: "proposed_price",
            headerName: "Proposed Sell Price",
            width: 217,
            editable: false,
        },
        {
            field: "liquidation_spread",
            headerName: "Spread",
            width: 130,
            editable: false,
        },

        {
            field: "proposed_units",
            headerName: "Proposed Units",
            width: 190,
            editable: false,
        },

        {
            field: "proposed_face_value",
            headerName: "Face Value",
            width: 180,
            editable: false,
        },

        {
            field: "dis_available",
            headerName: "Dis",
            width: 100,
            editable: false,
            renderCell: (params: any) => {
                const value = params.value;
                const isTrue =
                    value === true ||
                    value === "true" ||
                    value === "True" ||
                    value === 1;
                return <span>{isTrue ? "Yes" : "No"}</span>;
            },
        },
        {
            field: "smest_id",
            headerName: "Smest ID",
            width: 150,
            editable: false,
            valueGetter: (params: any) => {
                return params.row?.smest_id || "";
            },
        },
        {
            field: "lead_owner_name",
            headerName: "Lead Owner",
            width: 180,
            editable: false,
            valueGetter: (params: any) => {
                return params.row?.leads_detail?.leadowner || "";
            },
        },


        {
            field: "liquidation_status",
            headerName: "Status",
            width: 180,
            renderCell: (params: any) => {
                const { uuid, liquidation_status } = params.row;

                if (updatingRow === uuid) {
                    return (
                        <div style={{ width: "100%", textAlign: "center" }}>
                            <CircularProgress size={25} />
                        </div>
                    );
                }

                return (
                    <Select
                        size="small"
                        value={liquidation_status || ""}
                        sx={{ width: "100%" }}
                        onChange={e => {
                            const newStatus = e.target.value as string;
                            openConfirmDialog(uuid, newStatus);
                        }}
                    >
                        <MenuItem value="Requested">Requested</MenuItem>
                        <MenuItem value="Executed">Executed</MenuItem>
                        <MenuItem value="Nonviable">Nonviable</MenuItem>
                    </Select>
                );
            },
        }
    ];

    const breakdown = getBreakdownByUserType();

    const userTypeLabel: Record<string, string> = {
        partner: "Partner",
        client: "Client",
        smest: "SMEST",
        unknown: "Unknown",
    };

    return (
        <Grid className="QuotesDataGrid">
            <Paper>
                <Grid style={{ margin: "20px 0", padding: "0 20px" }}>
                    <Grid container alignItems="center" spacing={3}>
                        <Grid item xs>
                            <div style={{ fontSize: "36px", float: "left" }}>Liquidation List</div>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="outlined"
                                startIcon={<BarChartOutlinedIcon />}
                                onClick={() => setBreakdownDialogOpen(true)}
                                style={{ textTransform: "none", fontWeight: 500, backgroundColor: "#05053bff", color: "white" }}
                            >
                                View Summary
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>

                {!loading && (
                    <Grid container spacing={3} style={{ padding: "0 80px 15px" }}>
                        <Grid item xs={12} sm={6} md={3}>
                            <Paper style={{ padding: 20 }}>
                                <div style={{ fontSize: "16px", fontWeight: 500 }}>
                                    Total Liquidation Requests
                                </div>

                                <h2 style={{ margin: "10px 0 0 0" }}>₹{totalLiquidationRequests}</h2>

                                <div style={{ textAlign: "right" }}>
                                    <CurrencyRupeeIcon
                                        style={{
                                            fontSize: 32,
                                            background: "white",
                                            color: "black",
                                            borderRadius: "50%",
                                            padding: 2,
                                        }}
                                    />
                                </div>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Paper style={{ padding: 20 }}>
                                <div style={{ fontSize: "16px", fontWeight: 500 }}>
                                    Total Requests
                                </div>

                                <h2 style={{ margin: "10px 0 0 0" }}>{totalRequests}</h2>

                                <div style={{ textAlign: "right" }}>
                                    <InfoOutlinedIcon
                                        style={{
                                            fontSize: 32,
                                            background: "white",
                                            color: "black",
                                            borderRadius: "50%",
                                            padding: 2,
                                        }}
                                    />
                                </div>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Paper style={{ padding: 20 }}>
                                <div style={{ fontSize: "16px", fontWeight: 500 }}>
                                    Executed Requests
                                </div>

                                <h2 style={{ margin: "10px 0 0 0" }}>{executedRequests}</h2>

                                <div style={{ textAlign: "right" }}>
                                    <TaskAltOutlinedIcon
                                        style={{
                                            fontSize: 32,
                                            background: "white",
                                            color: "black",
                                            borderRadius: "50%",
                                            padding: 2,
                                        }}
                                    />
                                </div>
                            </Paper>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                            <Paper style={{ padding: 20 }}>
                                <div style={{ fontSize: "16px", fontWeight: 500 }}>
                                    Total Clients
                                </div>

                                <h2 style={{ margin: "10px 0 0 0" }}>{totalClients}</h2>

                                <div style={{ textAlign: "right" }}>
                                    <GroupOutlinedIcon
                                        style={{
                                            fontSize: 32,
                                            background: "white",
                                            color: "black",
                                            borderRadius: "50%",
                                            padding: 2,
                                        }}
                                    />
                                </div>
                            </Paper>
                        </Grid>
                    </Grid>
                )}

                {confirmStatusModal && (
                    <GenericDialog
                        show={confirmStatusModal}
                        heading="Confirm Status Update"
                        inputProps={`Are you sure you want to change status to "${selectedStatus}"?`}
                        onClose={() => setConfirmStatusModal(false)}
                        showCancelButton={true}
                        handleSubmit={confirmStatusUpdate}
                    />
                )}

                <Dialog
                    open={breakdownDialogOpen}
                    onClose={() => setBreakdownDialogOpen(false)}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        Summary by User Type
                        <IconButton onClick={() => setBreakdownDialogOpen(false)} size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent dividers>
                        <TableContainer component={Paper} elevation={0}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                                        <TableCell sx={{ fontWeight: "bold" }}>User Type</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }} align="right">Total Liquidation Requests (₹)</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }} align="right">Total Requests</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }} align="right">Executed Requests</TableCell>
                                        <TableCell sx={{ fontWeight: "bold" }} align="right">Total Clients</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {breakdown.map((row: any) => (
                                        <TableRow
                                            key={row.user_type}
                                            sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                                        >
                                            <TableCell component="th" scope="row" sx={{ fontWeight: 500 }}>
                                                {userTypeLabel[row.user_type] ?? row.user_type}
                                            </TableCell>
                                            <TableCell align="right">₹{row.totalLiquidationRequests.toLocaleString()}</TableCell>
                                            <TableCell align="right">{row.totalRequests}</TableCell>
                                            <TableCell align="right">{row.executedRequests}</TableCell>
                                            <TableCell align="right">{row.totalClients}</TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow sx={{ backgroundColor: "#fafafa", borderTop: "2px solid #e0e0e0" }}>
                                        <TableCell sx={{ fontWeight: "bold" }}>Total</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: "bold" }}>₹{totalLiquidationRequests.toLocaleString()}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: "bold" }}>{totalRequests}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: "bold" }}>{executedRequests}</TableCell>
                                        <TableCell align="right" sx={{ fontWeight: "bold" }}>{totalClients}</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </DialogContent>
                </Dialog>

                <Box
                    sx={{
                        height: "100vh",
                        width: "100%",
                        px: [2, 10],
                        "& .MuiDataGrid-columnHeaderTitle": {
                            fontWeight: "bold",
                        },
                    }}
                >
                    {!loading ? (
                        <DataGrid
                            pagination
                            components={{ Toolbar: GridToolbar }}
                            rows={liquidation}
                            columns={dataGridCells}
                            getRowId={(row) => row.uuid}
                        />
                    ) : (
                        <Box>
                            <Grid container spacing={3} style={{ marginBottom: 20 }}>
                                {[1, 2, 3, 4].map((item) => (
                                    <Grid item xs={12} sm={6} md={3} key={item}>
                                        <Skeleton
                                            height={130}
                                            animation="wave"
                                            style={{ borderRadius: 8 }}
                                        />
                                    </Grid>
                                ))}
                            </Grid>

                            <Skeleton height={50} animation="wave" />
                            <Skeleton height={50} animation="wave" />
                            <Skeleton height={50} animation="wave" />
                            <Skeleton height={50} animation="wave" />
                            <Skeleton height={50} animation="wave" />
                        </Box>
                    )}
                </Box>
            </Paper>
        </Grid>
    );
};

export default LiquidationList;
